import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Property } from '../types';
import { parseDate } from './firestoreUtils';

export interface PropertySubmission {
  id: string;
  property: Property;
  submittedBy: {
    uid: string;
    name: string;
    email: string;
    phone: string;
  };
  approvalStatus: 'pending' | 'approved' | 'rejected';
  submittedAt: Date;
  reviewedAt?: Date;
  rejectionReason?: string;
}

const COLLECTION = 'propertySubmissions';

export async function createSubmission(
  property: Property,
  submittedBy: PropertySubmission['submittedBy']
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTION), {
    property: {
      ...property,
      createdAt: property.createdAt.toISOString(),
      updatedAt: property.updatedAt.toISOString(),
    },
    submittedBy,
    approvalStatus: 'pending',
    submittedAt: new Date().toISOString(),
  });
  // Admins are alerted by email (see ListProperty -> sendPropertySubmissionNotification)
  // and by the pending badge in the admin panel. Writing notification docs for
  // admins from the submitter's session is forbidden by the Firestore rules.
  return docRef.id;
}

export async function getPendingSubmissions(): Promise<PropertySubmission[]> {
  const q = query(
    collection(db, COLLECTION),
    where('approvalStatus', '==', 'pending'),
    orderBy('submittedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(docToSubmission);
}

export async function getAllSubmissions(): Promise<PropertySubmission[]> {
  const q = query(collection(db, COLLECTION), orderBy('submittedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(docToSubmission);
}

export async function getSubmissionsByStatus(
  status: 'pending' | 'approved' | 'rejected'
): Promise<PropertySubmission[]> {
  const q = query(
    collection(db, COLLECTION),
    where('approvalStatus', '==', status),
    orderBy('submittedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(docToSubmission);
}

export async function getSubmissionsByUser(uid: string): Promise<PropertySubmission[]> {
  const q = query(
    collection(db, COLLECTION),
    where('submittedBy.uid', '==', uid),
    orderBy('submittedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(docToSubmission);
}

export async function updateSubmission(submissionId: string, property: Property): Promise<void> {
  await updateDoc(doc(db, COLLECTION, submissionId), {
    property: {
      ...property,
      createdAt: property.createdAt.toISOString(),
      updatedAt: new Date().toISOString(),
    },
    approvalStatus: 'pending',
    submittedAt: new Date().toISOString(),
    rejectionReason: null,
    reviewedAt: null,
  });
}

async function writeUserNotification(
  uid: string,
  title: string,
  message: string,
  type: 'success' | 'error' | 'info'
): Promise<void> {
  try {
    await addDoc(collection(db, 'notifications'), {
      uid,
      title,
      message,
      type,
      read: false,
      createdAt: new Date().toISOString(),
    });
  } catch {
    // Non-critical — don't block approval/rejection
  }
}

export async function approveSubmission(submissionId: string, submission?: PropertySubmission): Promise<void> {
  await updateDoc(doc(db, COLLECTION, submissionId), {
    approvalStatus: 'approved',
    reviewedAt: new Date().toISOString(),
  });
  if (submission) {
    await writeUserNotification(
      submission.submittedBy.uid,
      'Property Approved',
      `Your property "${submission.property.title}" has been approved and is now live.`,
      'success'
    );
  }
}

export async function rejectSubmission(submissionId: string, reason: string, submission?: PropertySubmission): Promise<void> {
  await updateDoc(doc(db, COLLECTION, submissionId), {
    approvalStatus: 'rejected',
    reviewedAt: new Date().toISOString(),
    rejectionReason: reason,
  });
  if (submission) {
    await writeUserNotification(
      submission.submittedBy.uid,
      'Property Requires Changes',
      `Your property "${submission.property.title}" was not approved. Reason: ${reason}`,
      'error'
    );
  }
}

export interface UserNotification {
  id: string;
  uid: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'info';
  read: boolean;
  createdAt: Date;
}

export async function getUserNotifications(uid: string): Promise<UserNotification[]> {
  const q = query(
    collection(db, 'notifications'),
    where('uid', '==', uid),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      uid: data.uid,
      title: data.title,
      message: data.message,
      type: data.type,
      read: data.read,
      createdAt: parseDate(data.createdAt) || new Date(),
    };
  });
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await updateDoc(doc(db, 'notifications', notificationId), { read: true });
}

function docToSubmission(docSnap: any): PropertySubmission {
  const data = docSnap.data();
  const prop = data.property;
  return {
    id: docSnap.id,
    property: {
      ...prop,
      createdAt: parseDate(prop.createdAt) || new Date(),
      updatedAt: parseDate(prop.updatedAt) || new Date(),
    } as Property,
    submittedBy: data.submittedBy,
    approvalStatus: data.approvalStatus,
    submittedAt: parseDate(data.submittedAt) || new Date(),
    reviewedAt: data.reviewedAt ? parseDate(data.reviewedAt) || undefined : undefined,
    rejectionReason: data.rejectionReason,
  };
}
